'use client'

import type { ComponentProps, ReactNode } from 'react'

import { AlertDialog as AlertDialogPrimitive } from '@base-ui/react/alert-dialog'
import { InfoIcon, TrashIcon, WarningIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / AlertDialog. Figma: 10 / Components / Dialog (AlertDialog, AlertDialogMedia).
 * 480px wide, 24px padding and gap, 12px corners, Colors/surface/overlay in dark mode.
 * tone: destructive (delete), warning (permanent edit), info, or neutral (no media, for example discard changes).
 */
type AlertTone = 'destructive' | 'warning' | 'info' | 'neutral'

const AlertDialog = AlertDialogPrimitive.Root

function AlertDialogTrigger({ className, ...props }: Omit<AlertDialogPrimitive.Trigger.Props, 'className'> & { className?: string }) {
    return (
        <AlertDialogPrimitive.Trigger
            data-slot='alert-dialog-trigger'
            className={className}
            {...props}
        />
    )
}

function AlertDialogContent({ className, children, ...props }: Omit<AlertDialogPrimitive.Popup.Props, 'className'> & { className?: string }) {
    return (
        <AlertDialogPrimitive.Portal>
            <AlertDialogPrimitive.Backdrop className='fixed inset-0 z-50 bg-surface-scrim data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0' />
            <AlertDialogPrimitive.Popup
                data-slot='alert-dialog-content'
                className={cn(
                    'fixed top-1/2 left-1/2 z-50 flex w-[calc(100%-2rem)] max-w-120 -translate-x-1/2 -translate-y-1/2 flex-col gap-6 rounded-xl border border-border-default bg-surface-overlay p-6 text-text-primary shadow-overlay outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
                    className
                )}
                {...props}
            >
                {children}
            </AlertDialogPrimitive.Popup>
        </AlertDialogPrimitive.Portal>
    )
}

const mediaClasses: Record<Exclude<AlertTone, 'neutral'>, string> = {
    destructive: 'bg-status-error-background text-status-error-foreground',
    warning: 'bg-status-warning-background text-status-warning-foreground',
    info: 'bg-status-info-background text-status-info-foreground'
}

const mediaIcons: Record<Exclude<AlertTone, 'neutral'>, ReactNode> = {
    destructive: <TrashIcon aria-hidden='true' />,
    warning: <WarningIcon aria-hidden='true' />,
    info: <InfoIcon aria-hidden='true' />
}

/* AlertDialogMedia: 48px tinted circle with a 24px tone icon. */
function AlertDialogMedia({ tone = 'destructive', className, ...props }: ComponentProps<'span'> & { tone?: Exclude<AlertTone, 'neutral'> }) {
    return (
        <span
            data-slot='alert-dialog-media'
            data-tone={tone}
            className={cn('inline-flex size-12 shrink-0 items-center justify-center rounded-full [&_svg]:size-6', mediaClasses[tone], className)}
            {...props}
        >
            {mediaIcons[tone]}
        </span>
    )
}

interface AlertDialogHeaderProps extends ComponentProps<'div'> {
    tone?: AlertTone
}

function AlertDialogHeader({ tone = 'neutral', className, children, ...props }: AlertDialogHeaderProps) {
    return (
        <div
            data-slot='alert-dialog-header'
            className={cn('flex items-start gap-4', className)}
            {...props}
        >
            {tone !== 'neutral' && <AlertDialogMedia tone={tone} />}
            <div className='flex min-w-0 flex-col gap-2'>
                {children}
            </div>
        </div>
    )
}

function AlertDialogTitle({ className, ...props }: Omit<AlertDialogPrimitive.Title.Props, 'className'> & { className?: string }) {
    return (
        <AlertDialogPrimitive.Title
            data-slot='alert-dialog-title'
            className={cn('font-heading text-lg font-semibold text-text-primary', className)}
            {...props}
        />
    )
}

function AlertDialogDescription({ className, ...props }: Omit<AlertDialogPrimitive.Description.Props, 'className'> & { className?: string }) {
    return (
        <AlertDialogPrimitive.Description
            data-slot='alert-dialog-description'
            className={cn('text-base text-text-secondary', className)}
            {...props}
        />
    )
}

function AlertDialogFooter({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='alert-dialog-footer'
            className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:items-end sm:justify-end', className)}
            {...props}
        />
    )
}

const AlertDialogClose = AlertDialogPrimitive.Close

export {
    AlertDialog,
    AlertDialogClose,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogMedia,
    AlertDialogTitle,
    AlertDialogTrigger
}
export type { AlertTone }
